import Image from 'next/image';
import { useMemo } from 'react';

import { KeyValueRow, KeyValueTable } from '@/design-system/components/ui';
import useGetChainInfo from '@/hooks/useGetChainInfo';

import { Trigger } from '../../domain';
import { AddressChip } from './components/AddressChip';
import { CurrencyDisplay } from './components/CurrencyDisplay';

interface Props {
  trigger: Trigger;
}

export const TokenHolderTriggerFullDetails = ({ trigger }: Props) => {
  const { getChainIconUrl } = useGetChainInfo();

  const contract = useMemo(() => trigger.contracts?.[0], [trigger.contracts]);

  const contractDisplay = useMemo(() => {
    if (!contract) return null;

    return (
      <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
        <AddressChip address={contract.address} chainId={contract.chainId} />
      </div>
    );
  }, [contract]);

  const networkDisplay = useMemo(() => {
    if (!contract) return '-';

    const chainIconUrl = getChainIconUrl(contract.chainId);

    return (
      <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
        {chainIconUrl && <Image src={chainIconUrl} alt={contract.network} width={20} height={20} />}
        <span style={{ textTransform: 'capitalize' }}>{contract.network.replace(/-/g, ' ')}</span>
      </div>
    );
  }, [contract, getChainIconUrl]);

  const volumeCurrencyDisplay = useMemo(() => {
    if (!contract) return '-';
    if (!trigger.currencyVolumeExpression) return '-';

    return <CurrencyDisplay value={trigger.currencyVolumeExpression} chainId={contract.chainId} />;
  }, [trigger.currencyVolumeExpression, contract]);

  return (
    <KeyValueTable>
      <KeyValueRow label="Name" value={trigger.name} />
      <KeyValueRow label="Description" value={trigger.description} />
      <KeyValueRow label="Executed with" value="Snapshots of token holders for contract">
        <div className="tw:flex tw:flex-row tw:gap-1 tw:items-center tw:flex-wrap tw:ml-2">{contractDisplay}</div>
      </KeyValueRow>
      <KeyValueRow label="Network" value={networkDisplay} />
      <KeyValueRow label="Volume currency" value={volumeCurrencyDisplay} />
      <KeyValueRow label="Argument conditions" value={trigger.conditionExpression ?? '-'} />
    </KeyValueTable>
  );
};
